/**
 * Picture frames from win milestones (D-057, FigCraft "38 Win milestones"), drawn inside the picture's own circle so a
 * framed picture measures the same as a plain one. Liyelaa: a turned lacquer band, red with engraved gold lozenges
 * between two gold rims. Reed mat: gold and sand diamonds woven on dark reed. Below 40 px only the band and rims stay.
 */
import type { ReactElement } from "react";
import { isOccasionFrame, type FrameId } from "@digu/shared";
import { useOccasion } from "../lib/occasions";

const polar = (c: number, r: number, t: number): string => `${(c + r * Math.cos(t)).toFixed(2)},${(c + r * Math.sin(t)).toFixed(2)}`;

/** Four-point lozenges round the band: `half` is each one's angular half-width, `depth` its radial half-height. */
function lozenges(c: number, rm: number, n: number, shape: (i: number) => { depth: number; half: number; fill: string }): ReactElement[] {
  return Array.from({ length: n }, (_, i) => {
    const a = (2 * Math.PI * i) / n, { depth, half, fill } = shape(i);
    const pts = [polar(c, rm + depth, a), polar(c, rm, a + half), polar(c, rm - depth, a), polar(c, rm, a - half)].join(" ");
    return <polygon key={i} points={pts} fill={fill} />;
  });
}

/**
 * D-180: an occasion's frame (`frame-occ-<id>`): a ring in its colours, each colour an equal arc and the set going round
 * twice (like a flag's band), between two hairline rims. Its colours come with the occasion; nothing is drawn until the
 * occasion is known (other players' occasions are fetched on demand) or when it can't be found.
 */
export function OccasionFrame({ frame, size, colours: given }: { frame: string; size: number; colours?: string[] }): ReactElement | null {
  const occ = useOccasion(given ? null : frame.slice("frame-occ-".length));
  return <OccasionRing frame={frame} size={size} colours={given ?? occ?.frame.colours ?? []} />;
}

export function OccasionRing({ frame, size, colours }: { frame: string; size: number; colours: string[] }): ReactElement | null {
  if (colours.length < 2) return null;
  const b = Math.max(3, size * 0.09), c = size / 2, rm = c - b / 2, hair = Math.max(0.8, size / 90);
  const n = colours.length * 2, step = (2 * Math.PI) / n;
  const arcs = Array.from({ length: n }, (_, i) => {
    const a0 = -Math.PI / 2 + i * step, a1 = a0 + step;
    const d = `M ${polar(c, rm, a0)} A ${rm.toFixed(2)} ${rm.toFixed(2)} 0 0 1 ${polar(c, rm, a1)}`;
    return <path key={i} d={d} fill="none" stroke={colours[i % colours.length]} strokeWidth={b} data-colour={colours[i % colours.length]} />;
  });
  return (
    <svg className="picture-frame picture-frame--occasion" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" data-frame={frame}>
      {arcs}
      <circle cx={c} cy={c} r={c - hair / 2} fill="none" stroke="var(--frame-gold)" strokeWidth={hair} />
      <circle cx={c} cy={c} r={c - b + hair / 2} fill="none" stroke="var(--frame-gold)" strokeWidth={hair} />
    </svg>
  );
}

export function PictureFrame({ frame, size }: { frame: FrameId; size: number }): ReactElement | null {
  if (isOccasionFrame(frame)) return <OccasionFrame frame={frame} size={size} />;
  const b =Math.max(3, size * 0.09), c = size / 2, rm = c - b / 2, hair = Math.max(0.8, size / 90), detail = size >= 40;
  const rim = frame === "liyelaa" ? "var(--frame-gold)" : "var(--frame-sand)";
  let pattern: ReactElement[] = [];
  if (detail && frame === "liyelaa") {
    const n = Math.max(12, Math.floor(Math.round((2 * Math.PI * rm) / (b * 1.5)) / 2) * 2);
    pattern = lozenges(c, rm, n, (i) => (i % 2 === 0 ? { depth: b * 0.36, half: (b * 0.2) / rm, fill: "var(--frame-gold)" } : { depth: b * 0.14, half: (b * 0.14) / rm, fill: "var(--frame-gold)" }));
  } else if (detail) {
    const n = Math.max(12, Math.floor(Math.round((2 * Math.PI * rm) / (b * 0.95)) / 2) * 2);
    pattern = lozenges(c, rm, n, (i) => ({ depth: b * 0.42, half: (Math.PI / n) * 0.92, fill: i % 2 === 0 ? "var(--frame-gold)" : "var(--frame-sand)" }));
  }
  return (
    <svg className="picture-frame" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" data-frame={frame}>
      <circle cx={c} cy={c} r={rm} fill="none" stroke={frame === "liyelaa" ? "var(--frame-lacquer)" : "var(--frame-reed)"} strokeWidth={b} />
      {pattern}
      <circle cx={c} cy={c} r={c - hair / 2} fill="none" stroke={rim} strokeWidth={hair} />
      <circle cx={c} cy={c} r={c - b + hair / 2} fill="none" stroke={rim} strokeWidth={hair} />
    </svg>
  );
}
